import React, { useCallback, useEffect, useState } from 'react';
import {
  DropDown,
  SimpleModal,
  SimpleModalBody,
  SimpleModalHeader,
} from '@noths/polaris-client-ribbons-design-system';
import { pxToRem } from '@noths/polaris-client-styles';
import { Button } from '@noths/polaris-design-system-core-components';

import * as styles from 'client-site-chrome-ui/components/molecules/LocaleModal/styles/LocaleModal.styles';
import {
  countryOptions,
  currencyOptions,
} from 'client-site-chrome-ui/components/molecules/LocaleModal/utils';
import {
  decodeCountryValue,
  encodeCountryValue,
} from 'client-site-chrome-ui/components/molecules/LocaleModal/utils/dropdownOptions';
import type { Locale } from 'client-site-chrome-ui/types/locale';

export interface LocaleModalProps {
  isOpen: boolean;
  locale?: Locale;
  onClose: VoidFunction;
  onSave: (locale: Locale) => void;
}

const simpleModalContentStyle = { maxWidth: pxToRem(360) };

export const LocaleModal = ({ isOpen, locale, onClose, onSave }: LocaleModalProps) => {
  const [country, setCountry] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [hasFormSubmitted, setFormSubmitted] = useState(false);
  const hasCurrencyError = hasFormSubmitted && currency === '';
  const hasCountryError = hasFormSubmitted && country === '';
  const hasError = hasCountryError || hasCurrencyError;

  useEffect(() => {
    if (locale) {
      setCountry(encodeCountryValue(locale));
      setCurrency(locale.currency);
    } else {
      setCountry('');
      setCurrency('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const onSubmit = useCallback(
    (evt: React.FormEvent) => {
      evt.preventDefault();
      setFormSubmitted(true);
      if (!hasError) {
        const locale = {
          currency,
          ...decodeCountryValue(country),
        };
        onSave(locale);
      }
    },
    [country, currency, hasError, onSave],
  );

  const onCountryChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => setCountry(evt.currentTarget.value),
    [],
  );

  const onCurrencyChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => setCurrency(evt.currentTarget.value),
    [],
  );

  return (
    <SimpleModal
      contentLabel="Select Locale"
      contentStyles={simpleModalContentStyle}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <SimpleModalHeader onClose={onClose} title="Shopping preferences" />
      <SimpleModalBody>
        <form data-testid="update-locale-form" onSubmit={onSubmit}>
          <DropDown
            css={styles.dropDown}
            error={hasCountryError ? 'Please select a country' : null}
            id="country"
            label="Country"
            onChange={onCountryChange}
            options={countryOptions}
            value={country}
          />
          <DropDown
            css={styles.dropDown}
            error={hasCurrencyError ? 'Please select a currency' : null}
            id="currency"
            label="Currency"
            onChange={onCurrencyChange}
            options={currencyOptions}
            value={currency}
          />
          <Button>Save preferences</Button>
        </form>
      </SimpleModalBody>
    </SimpleModal>
  );
};
